import { Icon } from "@/components/Icon/Icon";
import { PilotLock } from "@/components/PilotLock";
import { OwnerPill } from "@/components/primitives";
import { useFixedIngredientsSearch } from "@/hooks/useFixedIngredientsSearch";
import { useFlavorSpecCatalog } from "@/hooks/useFlavorSpecCatalog";
import { logEvent } from "@/lib/eventLogger";
import { cn } from "@/utils/cn";
import type {
  FixedIngredientRef,
  FlavorSpecOption,
  FlavorSpecSection,
} from "@/utils/types/flavor";
import { useEffect, useMemo, useRef, useState } from "react";
import { OptionalSection } from "./OptionalSection";

// A section-or-option is locked when the row is published (returned by the
// catalog endpoint) but not yet switched on in Pilot. The server-side flag
// is the single source of truth — see public.flavor_spec_*.is_available.
const isLocked = (row: { isAvailable: boolean } | undefined | null): boolean =>
  row != null && row.isAvailable === false;

export type AdvancedSpecsValue = {
  basePresets: FixedIngredientRef[];
  inventoryOnly: boolean;
  regScope: string;
  claim: string | null;
  application: string | null;
  form: "liquid" | "powder" | null;
};

type AdvancedSpecsProps = {
  value: AdvancedSpecsValue;
  onChange: (next: AdvancedSpecsValue) => void;
  onLeadBaseChange?: () => void;
};

export function AdvancedSpecs({
  value,
  onChange,
  onLeadBaseChange,
}: AdvancedSpecsProps) {
  const { basePresets, inventoryOnly, regScope, claim, application, form } =
    value;

  const [open, setOpen] = useState(false);
  const [baseSearch, setBaseSearch] = useState("");
  const [baseSearchOpen, setBaseSearchOpen] = useState(false);
  const [appsExpanded, setAppsExpanded] = useState(false);

  const catalog = useFlavorSpecCatalog();
  const search = useFixedIngredientsSearch(baseSearch);

  // Initialise regScope from the catalog default once it loads.
  useEffect(() => {
    if (!regScope && catalog.data) {
      onChange({ ...value, regScope: catalog.data.defaultRegulatoryScopeCode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.data?.defaultRegulatoryScopeCode]);

  const sectionsByCode = useMemo(() => {
    const map = new Map<string, FlavorSpecSection>();
    for (const s of catalog.data?.sections ?? []) map.set(s.code, s);
    return map;
  }, [catalog.data]);

  const regSection = sectionsByCode.get("regulatory_scope");
  const appSection = sectionsByCode.get("application");
  const claimsSection = sectionsByCode.get("claims");
  const formSection = sectionsByCode.get("form");
  const invSection = sectionsByCode.get("inventory_restriction");

  const availableClaims: FlavorSpecOption[] = useMemo(() => {
    if (!claimsSection) return [];
    return claimsSection.options.filter((o) => o.parentOptionCode === regScope);
  }, [claimsSection, regScope]);

  // Clear claim if it's no longer valid for the new scope.
  useEffect(() => {
    if (!claim) return;
    const valid = availableClaims.some((o) => o.code === claim);
    if (!valid) onChange({ ...value, claim: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regScope, availableClaims]);

  const set = (patch: Partial<AdvancedSpecsValue>) =>
    onChange({ ...value, ...patch });

  // Lead base preset → drive recommendation pulse on first preset change.
  const prevLeadKey = useRef<string | null>(null);
  useEffect(() => {
    const lead = basePresets[0];
    const key = lead ? `${lead.kind}:${lead.id}` : null;
    if (key !== prevLeadKey.current) {
      prevLeadKey.current = key;
      if (key && onLeadBaseChange) onLeadBaseChange();
    }
  }, [basePresets, onLeadBaseChange]);

  // Disclosure summary chips.
  const summary = useMemo(() => {
    const bits: string[] = [];
    basePresets.forEach((b) => bits.push(b.title));
    if (inventoryOnly) bits.push("Inventory only");
    const scopeLabel = regSection?.options.find(
      (o) => o.code === regScope
    )?.label;
    if (scopeLabel) bits.push(scopeLabel);
    if (claim) {
      const claimLabel = availableClaims.find((o) => o.code === claim)?.label;
      if (claimLabel) bits.push(claimLabel);
    }
    if (application) {
      const appLabel = appSection?.options.find(
        (o) => o.code === application
      )?.label;
      if (appLabel) bits.push(appLabel);
    }
    if (form) bits.push(form === "liquid" ? "Liquid" : "Powder");
    return bits;
  }, [
    basePresets,
    inventoryOnly,
    regScope,
    claim,
    application,
    form,
    regSection,
    appSection,
    availableClaims,
  ]);

  // Search results — exclude already-added refs.
  const searchResults: FixedIngredientRef[] = useMemo(() => {
    const rows = search.data ?? [];
    const taken = new Set(basePresets.map((b) => `${b.kind}:${b.id}`));
    return rows.filter((r) => !taken.has(`${r.kind}:${r.id}`));
  }, [search.data, basePresets]);

  const iconForKind = (kind: FixedIngredientRef["kind"]) =>
    kind === "food" ? "sprout" : kind === "flavor" ? "flask" : "box";

  return (
    <div className={cn("advanced-disclosure", open && "open")}>
      <button
        type="button"
        onClick={() => {
          setOpen((o) => {
            // Fire only on the false → true transition (open intent).
            if (!o) logEvent("flavor_create_advanced_specs_opened");
            return !o;
          });
        }}
        className={cn(
          "advanced-toggle-btn",
          summary.length > 0 && !open && "with-summary"
        )}
      >
        <div className="hstack" style={{ gap: 12, width: "100%" }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            Advanced specifications
          </span>
          <span
            className="mono"
            style={{
              fontSize: 10,
              color: "var(--ink-4)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Optional
          </span>
          <span style={{ flex: 1 }} />
          {!open && summary.length === 0 && (
            <span style={{ fontSize: 12, color: "var(--ink-4)" }}>
              {catalog.isLoading
                ? "Loading specs…"
                : "Base, scope, applications, claims…"}
            </span>
          )}
          <span className={cn("advanced-chevron", open && "open")}>
            <Icon name="chevron" size={12} />
          </span>
        </div>
        {!open && summary.length > 0 && (
          <div className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
            {summary.map((bit) => (
              <span key={bit} className="chip">
                {bit}
              </span>
            ))}
          </div>
        )}
      </button>

      {open && (
        <div className="slide-up advanced-body">
          {/* FIXED INGREDIENT REQUIREMENTS — search-driven */}
          <OptionalSection
            title="Fixed ingredient requirements"
            hint="Set the ingredients the formulation must include."
          >
            <div className="vstack gap-2" style={{ minWidth: 0 }}>
              {basePresets.length > 0 && (
                <div className="vstack" style={{ gap: 6 }}>
                  {basePresets.map((bp) => (
                    <div
                      key={`${bp.kind}:${bp.id}`}
                      className="base-preset-row"
                    >
                      <div className="base-preset-icon">
                        <Icon name={iconForKind(bp.kind)} size={11} />
                      </div>
                      <div
                        className="hstack"
                        style={{
                          flex: 1,
                          minWidth: 0,
                          gap: 8,
                          alignItems: "baseline",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {bp.title}
                        </span>
                        {bp.subtitle && (
                          <span
                            className="mono"
                            style={{
                              fontSize: 10,
                              color: "var(--ink-4)",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {bp.subtitle}
                          </span>
                        )}
                      </div>
                      <OwnerPill owner={bp.owner} />
                      <button
                        type="button"
                        className="btn xs ghost"
                        title="Remove"
                        onClick={() =>
                          set({
                            basePresets: basePresets.filter(
                              (x) => !(x.kind === bp.kind && x.id === bp.id)
                            ),
                          })
                        }
                      >
                        <Icon name="x" size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="base-search-row">
                <Icon name="search" size={12} />
                <input
                  className="base-search-input"
                  value={baseSearch}
                  onChange={(e) => {
                    setBaseSearch(e.target.value);
                    setBaseSearchOpen(true);
                  }}
                  onFocus={() => setBaseSearchOpen(true)}
                  onBlur={() => setTimeout(() => setBaseSearchOpen(false), 150)}
                  placeholder="Search foods, flavors, inventory…"
                />
                {baseSearchOpen && baseSearch.trim().length > 0 && (
                  <div className="base-results">
                    {search.isLoading && (
                      <div
                        className="base-result-row"
                        style={{ pointerEvents: "none" }}
                      >
                        <span style={{ fontSize: 12, color: "var(--ink-4)" }}>
                          Searching…
                        </span>
                      </div>
                    )}
                    {!search.isLoading && searchResults.length === 0 && (
                      <div
                        className="base-result-row"
                        style={{ pointerEvents: "none" }}
                      >
                        <span style={{ fontSize: 12, color: "var(--ink-4)" }}>
                          No matches
                        </span>
                      </div>
                    )}
                    {searchResults.map((r) => (
                      <button
                        key={`${r.kind}:${r.id}`}
                        type="button"
                        className="base-result-row"
                        onMouseDown={() => {
                          set({ basePresets: [...basePresets, r] });
                          setBaseSearch("");
                          setBaseSearchOpen(false);
                        }}
                      >
                        <div className="base-result-icon">
                          <Icon name={iconForKind(r.kind)} size={11} />
                        </div>
                        <div
                          className="vstack"
                          style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.title}
                          </span>
                          {r.subtitle && (
                            <span
                              className="mono"
                              style={{
                                fontSize: 10,
                                color: "var(--ink-4)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {r.subtitle}
                            </span>
                          )}
                        </div>
                        <OwnerPill owner={r.owner} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </OptionalSection>

          {/* REGULATORY SCOPE */}
          {regSection && (
            <OptionalSection
              title={regSection.label}
              hint={regSection.hint ?? undefined}
            >
              <div className="regscope-grid">
                {regSection.options.map((o) => {
                  const sel = regScope === o.code;
                  const locked = isLocked(o);
                  const card = (
                    <label
                      key={o.code}
                      className={cn("regscope-card", sel && "selected")}
                    >
                      <input
                        type="radio"
                        name="regscope"
                        checked={sel}
                        disabled={locked}
                        onChange={() => !locked && set({ regScope: o.code })}
                        style={{
                          accentColor: "var(--ink)",
                          width: 14,
                          height: 14,
                        }}
                      />
                      <div
                        className="vstack"
                        style={{ flex: 1, lineHeight: 1.25 }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 500 }}>
                          {o.label}
                        </span>
                        {o.subtitle && (
                          <span
                            className="mono"
                            style={{ fontSize: 10, color: "var(--ink-4)" }}
                          >
                            {o.subtitle}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                  return locked ? (
                    <PilotLock key={o.code} block badge>
                      {card}
                    </PilotLock>
                  ) : (
                    card
                  );
                })}
              </div>
            </OptionalSection>
          )}

          {/* APPLICATION */}
          {appSection && (
            <OptionalSection
              title={appSection.label}
              hint={appSection.hint ?? undefined}
            >
              <div className="app-grid">
                {(appsExpanded
                  ? appSection.options
                  : appSection.options.slice(0, 9)
                ).map((o) => {
                  const sel = application === o.code;
                  const locked = isLocked(o);
                  const card = (
                    <button
                      key={o.code}
                      type="button"
                      disabled={locked}
                      className={cn("app-card", sel && "selected")}
                      onClick={() => !locked && set({ application: sel ? null : o.code })}
                    >
                      <div style={{ fontSize: 12, fontWeight: 500 }}>
                        {o.label}
                      </div>
                      {o.subtitle && (
                        <div
                          className="mono"
                          style={{
                            fontSize: 10,
                            color: "var(--ink-4)",
                            marginTop: 2,
                          }}
                        >
                          {o.subtitle}
                        </div>
                      )}
                    </button>
                  );
                  return locked ? (
                    <PilotLock key={o.code} block badge>
                      {card}
                    </PilotLock>
                  ) : (
                    card
                  );
                })}
              </div>
              {appSection.options.length > 9 && (
                <button
                  type="button"
                  onClick={() => setAppsExpanded((e) => !e)}
                  className="btn xs ghost"
                  style={{ marginTop: 8 }}
                >
                  {appsExpanded ? (
                    <>
                      Show less <Icon name="chevron" size={10} />
                    </>
                  ) : (
                    <>
                      See all ({appSection.options.length}){" "}
                      <Icon name="chevron" size={10} />
                    </>
                  )}
                </button>
              )}
            </OptionalSection>
          )}

          {/* INVENTORY */}
          {invSection && (
            <OptionalSection
              title={invSection.label}
              hint="Filters the ingredient pool to those you already have on hand"
            >
              <label className="inventory-toggle">
                <input
                  type="checkbox"
                  checked={inventoryOnly}
                  onChange={(e) => set({ inventoryOnly: e.target.checked })}
                  style={{
                    accentColor: "var(--brand-green)",
                    width: 14,
                    height: 14,
                  }}
                />
                <div className="vstack" style={{ flex: 1, lineHeight: 1.3 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {invSection.options[0]?.label ??
                      "Use only ingredients from the inventory"}
                  </span>
                </div>
              </label>
            </OptionalSection>
          )}

          {/* CLAIMS — section-level lock during Pilot. Individual option
              flags stay is_available=true; the section flag is the single
              source of truth for whether the whole surface is interactive. */}
          {claimsSection &&
            (isLocked(claimsSection) ? (
              <PilotLock block badge>
                <OptionalSection
                  title={claimsSection.label}
                  hint={claimsSection.hint ?? undefined}
                  disabled
                >
                  <div className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
                    {availableClaims.map((o) => (
                      <button
                        key={o.code}
                        type="button"
                        disabled
                        className={cn("claim-chip")}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </OptionalSection>
              </PilotLock>
            ) : (
              <OptionalSection
                title={claimsSection.label}
                hint={claimsSection.hint ?? undefined}
              >
                <div className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
                  {availableClaims.map((o) => {
                    const sel = claim === o.code;
                    return (
                      <button
                        key={o.code}
                        type="button"
                        className={cn("claim-chip", sel && "selected")}
                        onClick={() => set({ claim: sel ? null : o.code })}
                      >
                        {sel && <span style={{ marginRight: 6 }}>✓</span>}
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </OptionalSection>
            ))}

          {/* FORM — section-level lock during Pilot. Same shape as Claims. */}
          {formSection &&
            (isLocked(formSection) ? (
              <PilotLock block badge>
                <OptionalSection
                  title={formSection.label}
                  hint={formSection.hint ?? undefined}
                  last
                  disabled
                >
                  <div className="form-picker">
                    {formSection.options.map((o) => (
                      <button key={o.code} type="button" disabled>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>
                          {o.label}
                        </div>
                        {o.subtitle && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--ink-3)",
                              marginTop: 2,
                            }}
                          >
                            {o.subtitle}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </OptionalSection>
              </PilotLock>
            ) : (
              <OptionalSection
                title={formSection.label}
                hint={formSection.hint ?? undefined}
                last
              >
                <div className="form-picker">
                  {formSection.options.map((o) => {
                    const sel = form === o.code;
                    return (
                      <button
                        key={o.code}
                        type="button"
                        className={cn(sel && "selected")}
                        onClick={() =>
                          set({
                            form: sel ? null : (o.code as "liquid" | "powder"),
                          })
                        }
                      >
                        <div style={{ fontSize: 13, fontWeight: 500 }}>
                          {o.label}
                        </div>
                        {o.subtitle && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--ink-3)",
                              marginTop: 2,
                            }}
                          >
                            {o.subtitle}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </OptionalSection>
            ))}
        </div>
      )}
    </div>
  );
}
